import React, { useCallback, useEffect, useState } from 'react';
import { useCart } from '../context/CartContext';
import { products } from '../data/products';
import { ShoppingCart, ChevronDown, ChevronUp } from 'lucide-react';
export function CutsSlider() {
  const {
    addToCart
  } = useCart();
  const cuts = products.filter(p => p.category === 'cortes');
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [addedId, setAddedId] = useState<string | null>(null);
  const goTo = useCallback((index: number) => {
    if (isTransitioning || index === activeIndex) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveIndex(index);
      setIsTransitioning(false);
    }, 400);
  }, [activeIndex, isTransitioning]);
  const goNext = () => goTo((activeIndex + 1) % cuts.length);
  const goPrev = () => goTo((activeIndex - 1 + cuts.length) % cuts.length);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') goNext();
      if (e.key === 'ArrowUp') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goNext]);
  const handleAddToCart = (cut: (typeof cuts)[0]) => {
    addToCart(cut);
    setAddedId(cut.id);
    setTimeout(() => setAddedId(null), 1500);
  };
  const activeCut = cuts[activeIndex];
  return <section id="cortes" className="relative w-full overflow-hidden bg-[#0A0A0A]" style={{
    height: '75vh',
    minHeight: '500px',
    maxHeight: '750px'
  }} data-id="element-38">
      {/* Slides */}
      {cuts.map((cut, index) => <div key={cut.id} className="absolute inset-0 transition-opacity duration-700 ease-in-out" style={{
      opacity: index === activeIndex ? 1 : 0,
      zIndex: index === activeIndex ? 1 : 0
    }} data-id="element-39">
          <img src={cut.image} alt={cut.name} className="absolute inset-0 w-full h-full object-cover" style={{
        filter: 'contrast(1.15) brightness(0.7)'
      }} data-id="element-40" />
          <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 50% 50%, transparent 25%, rgba(0,0,0,0.65) 65%, rgba(0,0,0,0.92) 100%)'
      }} data-id="element-41" />
          <div className="absolute inset-0" style={{
        background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, transparent 45%)'
      }} data-id="element-42" />
          <div className="absolute inset-0" style={{
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, transparent 30%)'
      }} data-id="element-43" />
        </div>)}

      {/* Content */}
      <div className="absolute inset-0 z-10 pointer-events-none" data-id="element-44">
        {/* Top-right: name + description */}
        <div className="absolute top-0 right-0 p-6 md:p-8 lg:p-16 text-right pointer-events-none" style={{
        maxWidth: 'min(420px, 45vw)',
        opacity: isTransitioning ? 0 : 1,
        transform: isTransitioning ? 'translateY(-8px)' : 'translateY(0)',
        transition: 'opacity 0.4s ease, transform 0.4s ease'
      }} data-id="element-45">
          <p className="text-[9px] uppercase tracking-[0.35em] text-fire/80 mb-3 font-semibold" data-id="element-46">
            Corte {String(activeIndex + 1).padStart(2, '0')} /{' '}
            {String(cuts.length).padStart(2, '0')}
          </p>
          <h2 className="font-heading text-white leading-tight mb-3 break-words" style={{
          fontSize: 'clamp(1.6rem, 3.5vw, 3rem)',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          wordBreak: 'break-word'
        }} data-id="element-47">
            {activeCut.name}
          </h2>
          <p className="text-white/55 text-xs font-light italic leading-relaxed ml-auto" style={{
          maxWidth: '180px'
        }} data-id="element-48">
            {activeCut.description}
          </p>
        </div>

        {/* Bottom-left: price + CTA */}
        <div className="absolute bottom-0 left-0 p-6 md:p-8 lg:p-16 pointer-events-auto" style={{
        opacity: isTransitioning ? 0 : 1,
        transform: isTransitioning ? 'translateY(8px)' : 'translateY(0)',
        transition: 'opacity 0.4s ease, transform 0.4s ease'
      }} data-id="element-49">
          <p className="text-[9px] uppercase tracking-[0.3em] text-white/35 mb-1.5" data-id="element-50">
            Precio por unidad
          </p>
          <p className="font-heading font-bold text-fire mb-4" style={{
          fontSize: 'clamp(1.6rem, 3vw, 2.5rem)'
        }} data-id="element-51">
            ${activeCut.price.toFixed(2)}
          </p>
          <button onClick={() => handleAddToCart(activeCut)} className="flex items-center gap-2.5 px-6 py-3 bg-fire text-white text-[10px] uppercase tracking-[0.2em] font-semibold hover:bg-ember transition-colors" data-id="element-52">
            <ShoppingCart className="w-3 h-3" data-id="element-53" />
            {addedId === activeCut.id ? 'Agregado ✓' : 'Agregar al Carrito'}
          </button>
        </div>
      </div>

      {/* Left dots */}
      <div className="absolute left-4 md:left-6 lg:left-10 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-2.5" data-id="element-54">
        {cuts.map((_, index) => <button key={index} onClick={() => goTo(index)} aria-label={`Ver corte ${index + 1}`} data-id="element-55">
            <span className="block rounded-full transition-all duration-300" style={{
          width: index === activeIndex ? '6px' : '4px',
          height: index === activeIndex ? '6px' : '4px',
          backgroundColor: index === activeIndex ? '#C0390A' : 'rgba(255,255,255,0.3)'
        }} data-id="element-56" />
          </button>)}
      </div>

      {/* Right arrows */}
      <div className="absolute right-4 md:right-6 lg:right-10 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-2" data-id="element-57">
        <button onClick={goPrev} className="p-1.5 text-white/30 hover:text-white transition-colors" aria-label="Anterior" data-id="element-58">
          <ChevronUp className="w-4 h-4" data-id="element-59" />
        </button>
        <button onClick={goNext} className="p-1.5 text-white/30 hover:text-white transition-colors" aria-label="Siguiente" data-id="element-60">
          <ChevronDown className="w-4 h-4" data-id="element-61" />
        </button>
      </div>

      {/* Section label */}
      <div className="absolute top-6 left-6 md:left-8 lg:left-16 z-20" data-id="element-62">
        <p className="text-[9px] uppercase tracking-[0.3em] text-white/25 font-medium" data-id="element-63">
          — Cortes Premium
        </p>
      </div>
    </section>;
}